import { Trans } from "@lingui/react/macro"
import { IconUsers } from "@tabler/icons-react"
import { useMemo } from "react"
import { resolveSpeakerColor, speakerInkColor, speakerTextColor } from "../../../../../lib/speaker-colors"
import { speakerVMFromName } from "../../../../../lib/speaker-vm"
import { EditableAnalysisField } from "../../../../EditableAnalysisField"
import { EditableField } from "../../../../EditableField"
import { SpeakerChip } from "../../../../SpeakerChip"
import { talkPercent } from "../../../helpers"
import classes from "../../../styles.module.css"
import type { IViewProps } from "./interfaces"

/**
 * AttendeesCardView renders one card per analyser-detected contributor with
 * their avatar, role, highlight quote, talk-share %, and action-item count.
 *
 * Dumb view: props in, events out. The owner-name edit fires `onOwnerSave`;
 * the smart wrapper (../index.tsx) owns the company API + persistence.
 *
 * Owner colour is shared with the player legend / action-item avatars so
 * each person reads the same across the page. Action counts are derived by
 * exact-name match against `actionPoints[].owner` — owner is a free-form
 * string in the analysis schema, so name-normalisation lives upstream.
 */
export function AttendeesCardView({
  attendees,
  speakerNames,
  talkShare,
  speakerColorMap,
  actionPoints,
  editContext,
  onOwnerSave,
}: IViewProps): React.JSX.Element | null {
  const talk = useMemo(() => talkPercent(speakerNames, talkShare), [speakerNames, talkShare])
  // Count action items per owner (by exact name match — owner is a free-form string).
  const actionCounts = useMemo(() => {
    const map = new Map<string, number>()
    for (const ap of actionPoints) {
      if (!ap.owner) continue
      map.set(ap.owner, (map.get(ap.owner) ?? 0) + 1)
    }
    return map
  }, [actionPoints])

  if (!attendees || attendees.length === 0) return null

  return (
    <section className={classes.card} data-testid="attendees-card">
      <div className={classes.cardHead}>
        <h2 className={classes.cardTitle}>
          <IconUsers size={16} />
          <Trans>Attendees</Trans>
        </h2>
        <span className={classes.cardCount}>{attendees.length}</span>
      </div>
      <div className={classes.attendeesGrid}>
        {attendees.map((a, i) => {
          const color = resolveSpeakerColor(a.name, speakerColorMap)
          const textColor = speakerTextColor(a.name, speakerColorMap)
          const ink = speakerInkColor(a.name, speakerColorMap)
          const pct = talk.get(a.name)
          const actions = actionCounts.get(a.name) ?? 0
          const namePath = `attendee_contributions.${i}.name`
          const rolePath = `attendee_contributions.${i}.role`
          const highlightsPath = `attendee_contributions.${i}.highlights`
          return (
            <div
              key={`${i}-${a.name}`}
              className={classes.attendeeCard}
              data-testid="attendee-card"
              style={{
                ["--owner-color" as string]: color,
                ["--owner-text" as string]: textColor,
                ["--owner-ink" as string]: ink,
              }}
            >
              <div className={classes.attendeeTop}>
                <SpeakerChip variant="avatar" size="lg" speaker={speakerVMFromName(a.name, speakerColorMap)} />
                <div className={classes.attendeeIdentity}>
                  <div className={classes.attendeeName}>
                    {editContext?.canEdit ? (
                      <EditableField
                        value={a.name}
                        onSave={(v) => onOwnerSave(namePath, v)}
                        size="xs"
                        c="var(--lex-text-info)"
                        underline="dotted"
                        width={120}
                      />
                    ) : (
                      a.name
                    )}
                  </div>
                  {(a.role || editContext) && (
                    <div className={classes.attendeeRole}>
                      {editContext ? (
                        <EditableAnalysisField
                          meetingId={editContext.meetingId}
                          fieldPath={rolePath}
                          value={a.role || ""}
                          canEdit={editContext.canEdit}
                          isEdited={!!editContext.fieldEdits[rolePath]}
                          onSaved={editContext.onEdited}
                        />
                      ) : (
                        a.role
                      )}
                    </div>
                  )}
                </div>
              </div>
              {(a.highlights || editContext) && (
                <p className={classes.attendeeQuote}>
                  {editContext ? (
                    <EditableAnalysisField
                      meetingId={editContext.meetingId}
                      fieldPath={highlightsPath}
                      value={a.highlights || ""}
                      canEdit={editContext.canEdit}
                      isEdited={!!editContext.fieldEdits[highlightsPath]}
                      onSaved={editContext.onEdited}
                    />
                  ) : (
                    a.highlights
                  )}
                </p>
              )}
              <div className={classes.attendeeMeta}>
                {pct !== undefined && (
                  <span>
                    <strong>{pct}%</strong> <Trans>talk-share</Trans>
                  </span>
                )}
                <span>
                  <strong>{actions}</strong> <Trans>actions</Trans>
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}
